import { useEffect, useState } from 'react'
import { Button, DatePicker, Input, InputNumber, Select, Spin, Switch, Tag } from 'antd'
import { EditOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import PoolSelectDialog from '../PoolSelectDialog'
import { getPoolDetail } from '@/api/pool'
import { extractStrategyParams } from '@/api'
import type { StockPool, StrategyVersion, StrategyParam } from '@/types'

export type PortfolioMarket = 'CN' | 'HK' | 'US'

export interface ConfigValue {
    // 基本设置
    name: string
    market: PortfolioMarket
    startDate: string
    cash: number
    description: string
    benchmark: string
    stockPoolId: number | null
    stockPoolName: string
    // 风控约束
    maxSingleWeight: number
    sectorLimit: number
    maxDrawdown: number
    cashReserve: number
    // AI 授权范围
    maxPositions: number
    positionSizing: string
    rebalanceFrequency: 'daily' | 'weekly' | 'monthly'
    // 策略 Skill 参数（由所选策略版本快照的 AI 提取参数填充，可调整）
    strategyParams: StrategyParam[]
    // 外部信号响应
    useMonitor: boolean
    useResearch: boolean
    reinvestDividend: boolean
}

export function createDefaultConfig(): ConfigValue {
    return {
        name: '',
        market: 'HK',
        startDate: dayjs().format('YYYY-MM-DD'),
        cash: 1000,
        description: '',
        benchmark: '',
        stockPoolId: null,
        stockPoolName: '',
        maxSingleWeight: 15,
        sectorLimit: 40,
        maxDrawdown: 20,
        cashReserve: 5,
        maxPositions: 20,
        positionSizing: 'equal_weight',
        rebalanceFrequency: 'daily',
        strategyParams: [],
        useMonitor: true,
        useResearch: true,
        reinvestDividend: false,
    }
}

const marketOptions: { value: PortfolioMarket; label: string }[] = [
    { value: 'HK', label: '港股 (HK)' },
    { value: 'CN', label: 'A股 (CN)' },
    { value: 'US', label: '美股 (US)' },
]

const benchmarkOptions = [
    { value: 'HK.800000', label: '恒生指数' },
    { value: 'SH.000300', label: '沪深300' },
    { value: 'US..NDX', label: '纳斯达克' },
    { value: 'US..SPX', label: '标普500' },
]

const positionSizingOptions = [
    { value: 'equal_weight', label: '等权分配' },
    { value: 'market_cap',   label: '市值加权' },
    { value: 'risk_parity',  label: '风险平价' },
]

const rebalanceFrequencyOptions = [
    { value: 'daily', label: '每日（当地收盘前 30–45 分钟）' },
    { value: 'weekly', label: '每周首次可执行日' },
    { value: 'monthly', label: '每月首次可执行日' },
]

interface Props {
    value: ConfigValue
    onChange: (v: ConfigValue) => void
    strategyVersion?: StrategyVersion | null
}

function PoolPreview({ poolId }: { poolId: number }) {
    const [detail, setDetail] = useState<StockPool | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        setDetail(null)
        getPoolDetail(poolId)
            .then(d => setDetail(d))
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [poolId])

    if (loading) return <div className="cfg-step__pool-preview"><Spin size="small" /></div>
    if (!detail) return null

    const stocks = detail.stocks ?? []
    const marketLabel = (code: string) =>
        ({ HK: '港股', US: '美股', SH: 'A股', SZ: 'A股' })[code.split('.')[0]] ?? '其他'

    const marketMap: Record<string, number> = {}
    for (const s of stocks) {
        const lbl = marketLabel(s.code)
        marketMap[lbl] = (marketMap[lbl] ?? 0) + 1
    }
    const markets = Object.entries(marketMap).sort((a, b) => b[1] - a[1])
    const sample = stocks.slice(0, 5)

    return (
        <div className="cfg-step__pool-preview">
            {detail.description && (
                <div className="cfg-step__pool-preview-desc">{detail.description}</div>
            )}
            <div className="cfg-step__pool-preview-stats">
                <span className="cfg-step__pool-preview-count">共 <strong>{detail.count ?? stocks.length}</strong> 只</span>
                {markets.map(([mkt, cnt]) => (
                    <span key={mkt} className="cfg-step__pool-preview-market">{mkt} {cnt}</span>
                ))}
            </div>
            {sample.length > 0 && (
                <div className="cfg-step__pool-preview-row">
                    <span className="cfg-step__pool-preview-row-label">部分标的</span>
                    <div className="cfg-step__pool-preview-stocks">
                        {sample.map(s => (
                            <span key={s.id} className="cfg-step__pool-preview-stock">
                                <span className="cfg-step__pool-preview-stock-code">{s.code}</span>
                                <span className="cfg-step__pool-preview-stock-name">{s.name}</span>
                            </span>
                        ))}
                        {stocks.length > 5 && (
                            <span className="cfg-step__pool-preview-stock-more">…共 {stocks.length} 只</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function ConfigStep({ value, onChange, strategyVersion }: Props) {
    const [poolOpen, setPoolOpen] = useState(false)
    const [paramsLoading, setParamsLoading] = useState(false)
    const set = <K extends keyof ConfigValue>(key: K) => (v: ConfigValue[K]) =>
        onChange({ ...value, [key]: v })

    const hasParams = (value.strategyParams?.length ?? 0) > 0

    // 点击「刷新」主动调用后端接口触发 AI 提取核心策略参数并回填
    const handleRefreshParams = async () => {
        if (!strategyVersion?.id) return
        setParamsLoading(true)
        try {
            const fresh = await extractStrategyParams(strategyVersion.id)
            const params = fresh.strategy_params?.params ?? []
            onChange({ ...value, strategyParams: params.map(p => ({ ...p })) })
        } catch {
            // 提取失败时保持现状，由用户重试
        } finally {
            setParamsLoading(false)
        }
    }

    const updateParam = (key: string, patch: Partial<StrategyParam>) => {
        onChange({
            ...value,
            strategyParams: value.strategyParams.map(p =>
                p.key === key ? { ...p, ...patch } : p
            ),
        })
    }

    return (
        <section className="cfg-step">

            {/* 板块一：基本设置 */}
            <div className="cfg-step__section">
                <div className="cfg-step__section-title">基本设置</div>
                <div className="cfg-step__grid cfg-step__grid--2">
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">组合名称 <span className="cfg-step__required">*</span></div>
                        <Input
                            placeholder="请输入组合名称，如：新能源精选组合"
                            value={value.name}
                            onChange={e => set('name')(e.target.value)}
                            maxLength={100}
                        />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">交易市场 <span className="cfg-step__required">*</span></div>
                        <Select
                            style={{ width: '100%' }}
                            options={marketOptions}
                            value={value.market}
                            onChange={set('market')}
                            disabled={Boolean(strategyVersion?.market)}
                        />
                        {strategyVersion?.market && (
                            <div className="cfg-step__section-hint" style={{ marginTop: 6 }}>
                                交易市场与所选策略版本一致（{strategyVersion.market}），不可在此修改。
                            </div>
                        )}
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">建仓开始时间 <span className="cfg-step__required">*</span></div>
                        <DatePicker
                            style={{ width: '100%' }}
                            value={value.startDate ? dayjs(value.startDate) : null}
                            onChange={(_, ds) => set('startDate')(ds as string)}
                        />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">初始资金（万元）<span className="cfg-step__required">*</span></div>
                        <InputNumber
                            style={{ width: '100%' }}
                            min={1}
                            precision={0}
                            value={value.cash}
                            onChange={v => set('cash')(v ?? 1000)}
                        />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">业绩基准</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="选择对标指数（可选）"
                            allowClear
                            options={benchmarkOptions}
                            value={value.benchmark || undefined}
                            onChange={v => set('benchmark')(v ?? '')}
                        />
                    </div>
                    <div className="cfg-step__field cfg-step__field--full">
                        <div className="cfg-step__label">组合描述</div>
                        <Input
                            placeholder="简述投研逻辑与目标…"
                            value={value.description}
                            onChange={e => set('description')(e.target.value)}
                        />
                    </div>
                </div>

                <div className="cfg-step__divider" />

                <div className="cfg-step__section-subtitle">
                    选择股票池 <span className="cfg-step__required">*</span>
                </div>
                <div className="cfg-step__pool-row">
                    {value.stockPoolId ? (
                        <>
                            <Tag color="blue" style={{ borderColor: 'var(--accent)', padding: '5px 10px', fontSize: 13 }}>
                                {value.stockPoolName}
                            </Tag>
                            <Button size="small" icon={<EditOutlined />} onClick={() => setPoolOpen(true)}>
                                更换股票池
                            </Button>
                        </>
                    ) : (
                        <div className="cfg-step__pool-empty">
                            <span className="cfg-step__pool-empty-text">尚未选择股票池，请先选择</span>
                            <Button size="small" type="primary" onClick={() => setPoolOpen(true)}>
                                选择股票池
                            </Button>
                        </div>
                    )}
                </div>
                {value.stockPoolId && <PoolPreview key={value.stockPoolId} poolId={value.stockPoolId} />}
                <PoolSelectDialog
                    open={poolOpen}
                    currentPoolId={value.stockPoolId}
                    onCancel={() => setPoolOpen(false)}
                    onConfirm={(id, name) => {
                        onChange({ ...value, stockPoolId: id, stockPoolName: name })
                        setPoolOpen(false)
                    }}
                />
            </div>

            {/* 板块二：风控约束 */}
            <div className="cfg-step__section">
                <div className="cfg-step__section-title">风控约束</div>
                <div className="cfg-step__section-hint">由风控团队针对本组合设定的硬性限制，触发后系统强制执行。</div>
                <div className="cfg-step__grid cfg-step__grid--2">
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">单股仓位上限（%）</div>
                        <InputNumber style={{ width: '100%' }} min={1} max={100}
                            value={value.maxSingleWeight} onChange={v => set('maxSingleWeight')(v ?? 15)} />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">单行业仓位上限（%）</div>
                        <InputNumber style={{ width: '100%' }} min={10} max={100} step={5}
                            value={value.sectorLimit} onChange={v => set('sectorLimit')(v ?? 40)} />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">最大回撤止损线（%）</div>
                        <InputNumber style={{ width: '100%' }} min={1} max={50}
                            value={value.maxDrawdown} onChange={v => set('maxDrawdown')(v ?? 20)}
                            addonAfter="触发后停止开仓" />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">最低现金留存（%）</div>
                        <InputNumber style={{ width: '100%' }} min={0} max={50}
                            value={value.cashReserve} onChange={v => set('cashReserve')(v ?? 5)} />
                    </div>
                </div>
            </div>

            {/* 板块三：AI 授权范围 */}
            <div className="cfg-step__section">
                <div className="cfg-step__section-title">
                    <RobotOutlined style={{ marginRight: 6 }} />
                    AI 授权范围
                </div>
                <div className="cfg-step__section-hint">
                    定义 AI 经理的操作边界。以下参数由策略版本建议，可在授权范围内调整。
                </div>
                <div className="cfg-step__grid cfg-step__grid--2">
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">最大持仓数量</div>
                        <InputNumber style={{ width: '100%' }} min={1} max={100}
                            value={value.maxPositions} onChange={v => set('maxPositions')(v ?? 20)} />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">仓位分配方式</div>
                        <Select style={{ width: '100%' }} options={positionSizingOptions}
                            value={value.positionSizing} onChange={set('positionSizing')} />
                    </div>
                    <div className="cfg-step__field">
                        <div className="cfg-step__label">AI 决策频率</div>
                        <Select style={{ width: '100%' }} options={rebalanceFrequencyOptions}
                            value={value.rebalanceFrequency} onChange={set('rebalanceFrequency')} />
                    </div>
                </div>
                <div className="cfg-step__ai-skill">
                    <div className="cfg-step__ai-skill-head">
                        <span className="cfg-step__ai-skill-title">策略核心参数</span>
                        <Button
                            size="small"
                            type="text"
                            icon={<ReloadOutlined />}
                            loading={paramsLoading}
                            onClick={handleRefreshParams}
                            disabled={!strategyVersion?.id}
                        >
                            刷新
                        </Button>
                    </div>
                    <div className="cfg-step__section-hint">
                        由 AI 从所选策略版本快照自动提取的核心筛选参数，可在此调整后随组合保存。
                    </div>

                    {paramsLoading ? (
                        <div className="cfg-step__ai-skill-pending">
                            <Spin size="small" />
                            <span>AI 正在提取核心策略参数…</span>
                        </div>
                    ) : hasParams ? (
                        <div className="cfg-step__ai-skill-grid">
                            {value.strategyParams.map(p => (
                                <div key={p.key} className="cfg-step__ai-skill-item">
                                    <div className="cfg-step__ai-skill-item-label">
                                        {p.name}
                                        {p.unit && <span className="cfg-step__ai-skill-item-unit">（{p.unit}）</span>}
                                    </div>
                                    <div className="cfg-step__ai-skill-item-inputs">
                                        {p.type === 'range' && (
                                            <>
                                                <InputNumber
                                                    size="small" style={{ width: '100%' }}
                                                    placeholder="下限" precision={2}
                                                    addonBefore="≥"
                                                    value={p.min ?? undefined}
                                                    onChange={v => updateParam(p.key, { min: v ?? undefined })}
                                                />
                                                <span className="cfg-step__ai-skill-item-sep">~</span>
                                                <InputNumber
                                                    size="small" style={{ width: '100%' }}
                                                    placeholder="上限" precision={2}
                                                    addonBefore="≤"
                                                    value={p.max ?? undefined}
                                                    onChange={v => updateParam(p.key, { max: v ?? undefined })}
                                                />
                                            </>
                                        )}
                                        {p.type === 'min' && (
                                            <InputNumber
                                                size="small" style={{ width: '100%' }}
                                                placeholder="不低于" precision={2}
                                                addonBefore="≥"
                                                value={p.min ?? undefined}
                                                onChange={v => updateParam(p.key, { min: v ?? undefined })}
                                            />
                                        )}
                                        {p.type === 'max' && (
                                            <InputNumber
                                                size="small" style={{ width: '100%' }}
                                                placeholder="不超过" precision={2}
                                                addonBefore="≤"
                                                value={p.max ?? undefined}
                                                onChange={v => updateParam(p.key, { max: v ?? undefined })}
                                            />
                                        )}
                                    </div>
                                    {p.description && (
                                        <div className="cfg-step__ai-skill-item-desc">{p.description}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="cfg-step__ai-skill-empty">
                            该策略版本暂未提取到核心参数，可点击「刷新」重试。
                        </div>
                    )}
                </div>
            </div>

            {/* 板块四：外部信号响应 */}
            <div className="cfg-step__section">
                <div className="cfg-step__section-title">外部信号响应</div>
                <div className="cfg-step__section-hint">选择允许哪些外部信号触发 AI 重新评估组合。</div>
                <div className="cfg-step__switches">
                    {[
                        { key: 'useMonitor' as const,       label: '智能监控信号',   hint: '技术面或风险预警触发仓位调整' },
                        { key: 'useResearch' as const,      label: '投研 Agent 信号', hint: '基本面变化触发组合更新建议' },
                        { key: 'reinvestDividend' as const, label: '分红再投资',       hint: '现金分红自动买入同一标的' },
                    ].map(({ key, label, hint }) => (
                        <div key={key} className="cfg-step__switch-row">
                            <Switch checked={value[key] as boolean} onChange={v => set(key)(v as ConfigValue[typeof key])} />
                            <span className="cfg-step__switch-label">{label}</span>
                            <span className="cfg-step__switch-hint">· {hint}</span>
                        </div>
                    ))}
                </div>
            </div>

        </section>
    )
}
